/**
 * Ready-made elements: the things people add to a panel that are not a screenshot.
 *
 * All of it is our own drawing, made from primitives the renderer already supports — no imported files
 * and nothing traced from anyone. Each element is drawn inside a **unit box**, where `1` is the width
 * and `1` is the height, and the layer pipeline scales that into the layer's box. That is what makes
 * them scale cleanly and take a colour: an element is a shape, not a picture.
 *
 * The same function draws in the editor, in the browser export and on the server, because there is one
 * renderer — so a laurel badge cannot look like one thing while being edited and another in the file.
 */
import type { Ctx2D } from './env';
import type { ElementLayer } from '@keymaker/schema';

export type ElementCategory = 'award' | 'rating' | 'badge' | 'shape' | 'markup';

export interface ElementDefinition {
  id: string;
  label: string;
  category: ElementCategory;
  /** The box a freshly added element takes, as fractions of the canvas. */
  defaultBox: { width: number; height: number };
  /** A default fill, for elements whose look depends on it. */
  defaultFill?: string;
  /** Where a label sits inside the unit box, when the element can carry one. */
  labelBox?: { x: number; y: number; width: number; height: number };
  /** Default label text, so a new element is not an empty shape. */
  defaultLabel?: string;
  /** Default label size, as a fraction of canvas height, for a label that has to fit a small opening. */
  defaultLabelSize?: number;
  /** Default colour for an element's own inner detail, where its look depends on it. */
  defaultLabelColor?: string;
  /**
   * Draw inside a 0..1 × 0..1 box, with the context already scaled into it.
   *
   * `aspect` is that box's width over its height in real pixels. The unit box is *stretched* to the
   * box, which is what lets one element fill a wide banner and a square panel alike — but it also means
   * a circle drawn as a circle comes out an ellipse. Anything that has to stay round takes `aspect` and
   * divides its horizontal radii by it.
   */
  draw: (ctx: Ctx2D, layer: ElementLayer, aspect: number) => void;
}

const TAU = Math.PI * 2;

function path(ctx: Ctx2D, points: [number, number][]): void {
  ctx.beginPath();
  points.forEach(([x, y], index) => (index === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y)));
  ctx.closePath();
}

/** A visually round disc, whatever the box is stretched to. */
function disc(ctx: Ctx2D, cx: number, cy: number, radius: number, aspect: number): void {
  ctx.beginPath();
  ctx.ellipse(cx, cy, radius / aspect, radius, 0, 0, TAU);
  ctx.fill();
}

/** A ring: an outer disc with a hole, via even-odd so the middle is genuinely empty. */
function ring(
  ctx: Ctx2D,
  cx: number,
  cy: number,
  outer: number,
  inner: number,
  aspect: number,
): void {
  ctx.beginPath();
  ctx.ellipse(cx, cy, outer / aspect, outer, 0, 0, TAU);
  ctx.ellipse(cx, cy, inner / aspect, inner, 0, 0, TAU, true);
  ctx.fill();
}

/** A star of `tips` points, round rather than squashed by the box. */
function starPath(
  ctx: Ctx2D,
  cx: number,
  cy: number,
  outer: number,
  inner: number,
  aspect: number,
  tips = 5,
): void {
  ctx.beginPath();
  for (let i = 0; i < tips * 2; i += 1) {
    const radius = i % 2 === 0 ? outer : inner;
    const angle = -Math.PI / 2 + (i * Math.PI) / tips;
    const x = cx + (Math.cos(angle) * radius) / aspect;
    const y = cy + Math.sin(angle) * radius;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
}

/** A pointed leaf, drawn from its base along +x: two curves meeting at both ends. */
function leaf(ctx: Ctx2D, length: number, width: number): void {
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.quadraticCurveTo(length * 0.45, -width, length, 0);
  ctx.quadraticCurveTo(length * 0.45, width, 0, 0);
  ctx.closePath();
  ctx.fill();
}

/**
 * A pair of laurel branches, one up each side, opening in the middle for a label.
 *
 * Two earlier attempts are worth remembering. The first ran each branch from a bottom corner to the
 * opposite top corner, so they crossed in the middle and the result read as a bird. The second drew the
 * leaves as circles along an almost invisible stem, and at the size this is actually used the contact
 * sheet showed a row of specks.
 *
 * So: branches go *up* the sides and meet at the top, which is also what leaves room for words; the
 * leaves are pointed almonds rather than dots; and they overlap along the stem so the branch reads as
 * one thing instead of a dotted line.
 */
function laurelBranch(ctx: Ctx2D, aspect: number, spread = 1, leaves = 7): void {
  const base = { x: 0.47, y: 1 };
  const control = { x: 0.47 - 0.46 * spread, y: 0.92 };
  const tip = { x: 0.47 - 0.44 * spread, y: 0.04 };
  const at = (t: number): { x: number; y: number; angle: number } => {
    const x = (1 - t) * (1 - t) * base.x + 2 * (1 - t) * t * control.x + t * t * tip.x;
    const y = (1 - t) * (1 - t) * base.y + 2 * (1 - t) * t * control.y + t * t * tip.y;
    // The curve's own direction, so each leaf sits along the branch rather than beside it.
    const dx = 2 * (1 - t) * (control.x - base.x) + 2 * t * (tip.x - control.x);
    const dy = 2 * (1 - t) * (control.y - base.y) + 2 * t * (tip.y - control.y);
    return { x, y, angle: Math.atan2(dy, dx) };
  };

  const draw = (): void => {
    ctx.beginPath();
    ctx.moveTo(base.x, base.y);
    ctx.quadraticCurveTo(control.x, control.y, tip.x, tip.y);
    ctx.lineWidth = 0.035;
    ctx.strokeStyle = ctx.fillStyle as string;
    ctx.lineCap = 'round';
    ctx.stroke();

    for (let i = 0; i < leaves; i += 1) {
      const t = 0.04 + (i / (leaves - 1)) * 0.9;
      const point = at(t);
      // Longer at the base and shorter at the tip, the way a real sprig tapers.
      const width = 0.2 - t * 0.07;
      const length = (width * 2.5) / aspect;
      for (const side of [-1, 1]) {
        ctx.save();
        ctx.translate(point.x, point.y);
        // Fanned out from the stem and angled up it, which is how laurel leaves sit. The leaves facing
        // the middle are drawn at half size: at full size they reach across the opening, and the words
        // a badge exists to carry ended up lying on top of them.
        const inward = side * spread > 0;
        ctx.rotate(point.angle + side * 1.0);
        leaf(ctx, inward ? length * 0.5 : length, inward ? width * 0.5 : width);
        ctx.restore();
      }
    }
  };

  draw();
  ctx.save();
  ctx.translate(1, 0);
  ctx.scale(-1, 1);
  draw();
  ctx.restore();
}

/**
 * The catalogue.
 *
 * Boxes are fractions of the canvas, and the aspect they produce is the aspect the drawing expects —
 * a star in a square box, a laurel in a wide one. The iPhone family is 1320×2868, so a square box is
 * `width: w, height: w * 0.46`; the shapes take care of staying round regardless.
 */
export const ELEMENTS: ElementDefinition[] = [
  // ── awards ─────────────────────────────────────────────────────────────────────────
  {
    id: 'laurel-award',
    label: 'Laurel award',
    category: 'award',
    defaultBox: { width: 0.6, height: 0.15 },
    defaultLabel: "EDITOR'S CHOICE",
    // The size and the opening are chosen together. A caption-sized label is wider than the gap between
    // the branches, so it lay across the leaves and the ends were clipped — the badge has to be big
    // enough to hold its own words, and the words small enough to fit inside it.
    defaultLabelSize: 0.016,
    labelBox: { x: 0.26, y: 0.42, width: 0.48, height: 0.2 },
    draw: (ctx, _layer, aspect) => laurelBranch(ctx, aspect),
  },
  {
    id: 'laurel-wreath',
    label: 'Laurel wreath',
    category: 'award',
    defaultBox: { width: 0.44, height: 0.15 },
    defaultLabel: 'APP OF THE DAY',
    defaultLabelSize: 0.0115,
    labelBox: { x: 0.26, y: 0.48, width: 0.48, height: 0.16 },
    draw: (ctx, _layer, aspect) => {
      // Tighter than the award: the branches close over the top, which is what a wreath is.
      laurelBranch(ctx, aspect, 0.8, 6);
      starPath(ctx, 0.5, 0.08, 0.07, 0.03, aspect);
      ctx.fill();
    },
  },
  {
    id: 'badge-ribbon',
    label: 'Ribbon',
    category: 'award',
    defaultBox: { width: 0.34, height: 0.05 },
    labelBox: { x: 0.14, y: 0.24, width: 0.72, height: 0.5 },
    defaultLabel: 'PREMIUM',
    draw: (ctx) => {
      // Shallow notches: deep ones turn the banner into a bow tie, which is what the contact sheet
      // showed.
      path(ctx, [
        [0.03, 0],
        [0.97, 0],
        [0.92, 0.5],
        [0.97, 1],
        [0.03, 1],
        [0.08, 0.5],
      ]);
      ctx.fill();
    },
  },

  // ── ratings ────────────────────────────────────────────────────────────────────────
  {
    id: 'stars-5',
    label: 'Five stars',
    category: 'rating',
    defaultBox: { width: 0.3, height: 0.055 },
    defaultFill: '#F5B94A',
    labelBox: { x: 0, y: 0.66, width: 1, height: 0.3 },
    defaultLabel: '4.9 · 12K RATINGS',
    draw: (ctx, _layer, aspect) => {
      // Filled inside the loop. `starPath` begins a path, so filling once after the loop left four of
      // the five stars unpainted — the contact sheet showed one lone star.
      for (let i = 0; i < 5; i += 1) {
        starPath(ctx, 0.1 + i * 0.2, 0.3, 0.09, 0.038, aspect);
        ctx.fill();
      }
    },
  },
  {
    id: 'rate-pill',
    label: 'Rating pill',
    category: 'rating',
    defaultBox: { width: 0.22, height: 0.05 },
    defaultFill: '#101828',
    labelBox: { x: 0.2, y: 0.22, width: 0.76, height: 0.56 },
    defaultLabel: '4.9 ★',
    defaultLabelColor: '#FFFFFF',
    draw: (ctx, _layer, aspect) => {
      const radius = 0.5;
      ctx.beginPath();
      // A capsule: two half-circles joined by straight sides, rounded properly because the box is wide.
      ctx.moveTo(radius / aspect, 0);
      ctx.lineTo(1 - radius / aspect, 0);
      ctx.ellipse(1 - radius / aspect, 0.5, radius / aspect, radius, 0, -Math.PI / 2, Math.PI / 2);
      ctx.lineTo(radius / aspect, 1);
      ctx.ellipse(radius / aspect, 0.5, radius / aspect, radius, 0, Math.PI / 2, -Math.PI / 2);
      ctx.closePath();
      ctx.fill();
    },
  },

  // ── badges ─────────────────────────────────────────────────────────────────────────
  {
    id: 'badge-circle',
    label: 'Round badge',
    category: 'badge',
    defaultBox: { width: 0.12, height: 0.055 },
    labelBox: { x: 0.16, y: 0.38, width: 0.68, height: 0.24 },
    defaultLabel: 'NEW',
    draw: (ctx, layer, aspect) => {
      disc(ctx, 0.5, 0.5, 0.5, aspect);
      // The inner detail is drawn in the label colour rather than a translucent black, which is
      // invisible on a black badge — the contact sheet showed a plain disc.
      ctx.fillStyle = layer.labelColor ?? '#FFFFFF';
      disc(ctx, 0.5, 0.5, 0.4, aspect);
      ctx.fillStyle = layer.fill;
      disc(ctx, 0.5, 0.5, 0.34, aspect);
    },
  },
  {
    id: 'badge-shield',
    label: 'Shield',
    category: 'badge',
    defaultBox: { width: 0.11, height: 0.06 },
    draw: (ctx) => {
      ctx.beginPath();
      ctx.moveTo(0.5, 0);
      ctx.lineTo(1, 0.16);
      ctx.lineTo(1, 0.52);
      ctx.quadraticCurveTo(1, 0.92, 0.5, 1);
      ctx.quadraticCurveTo(0, 0.92, 0, 0.52);
      ctx.lineTo(0, 0.16);
      ctx.closePath();
      ctx.fill();
    },
  },

  // ── shapes ─────────────────────────────────────────────────────────────────────────
  {
    id: 'star',
    label: 'Star',
    category: 'shape',
    defaultBox: { width: 0.09, height: 0.041 },
    defaultFill: '#F5B94A',
    draw: (ctx, _layer, aspect) => {
      starPath(ctx, 0.5, 0.5, 0.5, 0.21, aspect);
      ctx.fill();
    },
  },
  {
    id: 'sparkle',
    label: 'Sparkle',
    category: 'shape',
    defaultBox: { width: 0.08, height: 0.037 },
    defaultFill: '#FFFFFF',
    draw: (ctx) => {
      // Four concave points: straight arms to the box's corners, curving back to the centre.
      ctx.beginPath();
      ctx.moveTo(0.5, 0);
      ctx.quadraticCurveTo(0.56, 0.44, 1, 0.5);
      ctx.quadraticCurveTo(0.56, 0.56, 0.5, 1);
      ctx.quadraticCurveTo(0.44, 0.56, 0, 0.5);
      ctx.quadraticCurveTo(0.44, 0.44, 0.5, 0);
      ctx.closePath();
      ctx.fill();
    },
  },
  {
    id: 'burst',
    label: 'Burst',
    category: 'shape',
    defaultBox: { width: 0.13, height: 0.06 },
    defaultFill: '#FFD166',
    draw: (ctx, _layer, aspect) => {
      const spikes = 14;
      ctx.beginPath();
      for (let i = 0; i < spikes * 2; i += 1) {
        const radius = i % 2 === 0 ? 0.5 : 0.34;
        const angle = (i * Math.PI) / spikes;
        const x = 0.5 + (Math.cos(angle) * radius) / aspect;
        const y = 0.5 + Math.sin(angle) * radius;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();
    },
  },
  {
    id: 'blob',
    label: 'Blob',
    category: 'shape',
    defaultBox: { width: 0.26, height: 0.07 },
    draw: (ctx) => {
      ctx.beginPath();
      ctx.moveTo(0.5, 0.02);
      ctx.bezierCurveTo(0.9, 0.04, 1, 0.4, 0.86, 0.72);
      ctx.bezierCurveTo(0.74, 1, 0.26, 0.98, 0.12, 0.7);
      ctx.bezierCurveTo(-0.02, 0.42, 0.1, 0.05, 0.5, 0.02);
      ctx.closePath();
      ctx.fill();
    },
  },
  {
    id: 'ring',
    label: 'Ring',
    category: 'shape',
    defaultBox: { width: 0.1, height: 0.046 },
    draw: (ctx, _layer, aspect) => ring(ctx, 0.5, 0.5, 0.5, 0.31, aspect),
  },
  {
    id: 'wave',
    label: 'Wave',
    category: 'shape',
    defaultBox: { width: 0.34, height: 0.045 },
    draw: (ctx) => {
      ctx.beginPath();
      ctx.moveTo(0, 0.5);
      for (let i = 0; i < 3; i += 1) {
        ctx.quadraticCurveTo(0.166 + i * 0.333, i % 2 === 0 ? 0 : 1, 0.333 + i * 0.333, 0.5);
      }
      ctx.lineTo(1, 1);
      ctx.lineTo(0, 1);
      ctx.closePath();
      ctx.fill();
    },
  },
  {
    id: 'hexagon',
    label: 'Hexagon',
    category: 'shape',
    defaultBox: { width: 0.09, height: 0.041 },
    draw: (ctx, _layer, aspect) => {
      path(
        ctx,
        Array.from({ length: 6 }, (_, i) => {
          const angle = -Math.PI / 2 + (i * TAU) / 6;
          return [0.5 + Math.cos(angle) * 0.5 / aspect, 0.5 + Math.sin(angle) * 0.5] as [number, number];
        }),
      );
      ctx.fill();
    },
  },
  {
    id: 'semicircle',
    label: 'Semicircle',
    category: 'shape',
    defaultBox: { width: 0.18, height: 0.041 },
    draw: (ctx, _layer, aspect) => {
      ctx.beginPath();
      ctx.ellipse(0.5, 1, 0.5 / aspect, 0.5, 0, Math.PI, TAU);
      ctx.closePath();
      ctx.fill();
    },
  },
  {
    id: 'plus',
    label: 'Plus',
    category: 'shape',
    defaultBox: { width: 0.08, height: 0.037 },
    draw: (ctx, _layer, aspect) => {
      // The arms are corrected so the cross stays even instead of stretching with the box.
      const arm = 0.5 / (aspect * 2.2);
      path(ctx, [
        [0.5 - arm, 0],
        [0.5 + arm, 0],
        [0.5 + arm, 0.5 - arm * aspect * 2.2 * 0.5],
        [1, 0.5 - arm * aspect * 2.2 * 0.5],
        [1, 0.5 + arm * aspect * 2.2 * 0.5],
        [0.5 + arm, 0.5 + arm * aspect * 2.2 * 0.5],
        [0.5 + arm, 1],
        [0.5 - arm, 1],
        [0.5 - arm, 0.5 + arm * aspect * 2.2 * 0.5],
        [0, 0.5 + arm * aspect * 2.2 * 0.5],
        [0, 0.5 - arm * aspect * 2.2 * 0.5],
        [0.5 - arm, 0.5 - arm * aspect * 2.2 * 0.5],
      ]);
      ctx.fill();
    },
  },

  // ── markup ─────────────────────────────────────────────────────────────────────────
  {
    id: 'arrow-right',
    label: 'Arrow',
    category: 'markup',
    defaultBox: { width: 0.13, height: 0.045 },
    draw: (ctx) => {
      path(ctx, [
        [0, 0.4],
        [0.58, 0.4],
        [0.58, 0.1],
        [1, 0.5],
        [0.58, 0.9],
        [0.58, 0.6],
        [0, 0.6],
      ]);
      ctx.fill();
    },
  },
  {
    id: 'arrow-curved',
    label: 'Curved arrow',
    category: 'markup',
    defaultBox: { width: 0.14, height: 0.07 },
    draw: (ctx) => {
      // A shaft sweeping up and to the right, ending in a head. Drawn as one outline so the whole
      // arrow takes one colour.
      const shaft = (offset: number): void => {
        ctx.beginPath();
        ctx.moveTo(0.04, 0.92 - offset);
        ctx.quadraticCurveTo(0.34, 0.3 + offset, 0.9, 0.3);
        ctx.lineTo(0.9, 0.3);
        ctx.strokeStyle = ctx.fillStyle as string;
        ctx.lineWidth = 0.05;
        ctx.lineCap = 'round';
        ctx.stroke();
      };
      shaft(0.12);
      path(ctx, [
        [0.72, 0.14],
        [1, 0.32],
        [0.74, 0.5],
      ]);
      ctx.fill();
    },
  },
  {
    id: 'underline',
    label: 'Underline',
    category: 'markup',
    defaultBox: { width: 0.28, height: 0.022 },
    draw: (ctx) => {
      ctx.beginPath();
      ctx.moveTo(0, 0.55);
      ctx.bezierCurveTo(0.28, 0, 0.68, 0.04, 1, 0.28);
      ctx.lineTo(0.98, 0.96);
      ctx.bezierCurveTo(0.66, 0.62, 0.3, 0.6, 0.02, 1);
      ctx.closePath();
      ctx.fill();
    },
  },
  {
    id: 'divider',
    label: 'Divider',
    category: 'markup',
    defaultBox: { width: 0.24, height: 0.017 },
    draw: (ctx, _layer, aspect) => {
      // A rule broken by a diamond: the shape a section break wants.
      const mid = 0.5;
      const gap = 0.05 / aspect;
      for (const [from, to] of [
        [0, mid - gap],
        [mid + gap, 1],
      ] as [number, number][]) {
        path(ctx, [
          [from, 0.42],
          [to, 0.42],
          [to, 0.58],
          [from, 0.58],
        ]);
        ctx.fill();
      }
      const half = 0.06 / aspect;
      path(ctx, [
        [mid, 0],
        [mid + half, 0.5],
        [mid, 1],
        [mid - half, 0.5],
      ]);
      ctx.fill();
    },
  },
  {
    id: 'checkmark',
    label: 'Check',
    category: 'markup',
    defaultBox: { width: 0.08, height: 0.037 },
    defaultFill: '#1B6B4A',
    defaultLabelColor: '#FFFFFF',
    draw: (ctx, layer, aspect) => {
      disc(ctx, 0.5, 0.5, 0.5, aspect);
      // The tick is drawn in the label colour, defaulting to a dark ink rather than a translucent
      // black: on a black disc the first version's tick was invisible.
      ctx.fillStyle = layer.labelColor ?? '#0B0F1A';
      path(ctx, [
        [0.24, 0.52],
        [0.42, 0.7],
        [0.78, 0.3],
        [0.86, 0.4],
        [0.43, 0.84],
        [0.16, 0.6],
      ]);
      ctx.fill();
    },
  },
  {
    id: 'quote-mark',
    label: 'Quote mark',
    category: 'markup',
    defaultBox: { width: 0.1, height: 0.046 },
    draw: (ctx, _layer, aspect) => {
      // Two filled commas, which read as a quotation at any size and take one colour.
      for (const offset of [0, 0.5]) {
        ctx.beginPath();
        ctx.moveTo(offset + 0.05, 0.06);
        ctx.quadraticCurveTo(offset + 0.45, 0.06, offset + 0.42, 0.94);
        ctx.quadraticCurveTo(offset + 0.18, 0.94, offset + 0.05, 0.06 / aspect);
        ctx.closePath();
        ctx.fill();
      }
    },
  },
];

const byId = new Map(ELEMENTS.map((element) => [element.id, element]));

export function elementById(id: string): ElementDefinition | undefined {
  return byId.get(id);
}

/** Grouped for a picker, in the order the catalogue declares. */
export function elementsByCategory(): { category: ElementCategory; label: string; items: ElementDefinition[] }[] {
  const names: Record<ElementCategory, string> = {
    award: 'Awards',
    rating: 'Ratings',
    badge: 'Badges',
    shape: 'Shapes',
    markup: 'Markup',
  };
  const order: ElementCategory[] = ['award', 'rating', 'badge', 'shape', 'markup'];
  return order
    .map((category) => ({
      category,
      label: names[category],
      items: ELEMENTS.filter((element) => element.category === category),
    }))
    .filter((group) => group.items.length > 0);
}
